'use client';

import { useCallback, useRef } from 'react';

/** How long to wait for a closing overlay to finish unmounting. */
const OVERLAY_TIMEOUT_MS = 800;
/** How long to keep watching for the restored control being torn down. */
const TEARDOWN_TIMEOUT_MS = 3000;

const OVERLAY_SELECTOR = '[role="dialog"],[role="alertdialog"],[role="menu"]';

/**
 * Puts keyboard focus back where it came from after a dialog closes.
 *
 * Radix does this by itself when a dialog is opened through <DialogTrigger>.
 * Dialogs driven purely by state have no trigger to restore to, so closing one
 * leaves focus on <body> and the keyboard user restarts from the top of the
 * document. Capture the control that opened the dialog, then restore it.
 *
 *   const { capture, restore } = useReturnFocus();
 *   <button onClick={(e) => { capture(e.currentTarget); setOpen(true); }} />
 *   <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) restore(); }} />
 */
export function useReturnFocus() {
  const triggerRef = useRef<HTMLElement | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  // Deliberately not torn down on unmount: deleting a post unmounts the very
  // component that asked for the restore, and that is exactly the case where
  // focus still needs somewhere to go. Both waits are capped, so an orphaned
  // one clears itself within a few seconds.
  const cancel = useCallback(() => {
    cleanupRef.current?.();
    cleanupRef.current = null;
  }, []);

  /** Pass the control being clicked; falls back to whatever has focus now. */
  const capture = useCallback((element?: HTMLElement | null) => {
    triggerRef.current =
      element ?? (document.activeElement as HTMLElement | null);
  }, []);

  /**
   * @param fallback Where to land when the trigger no longer exists — a delete
   *   confirmation destroys the very control that opened it, so without this
   *   focus would be lost exactly when it matters most.
   */
  const restore = useCallback(
    (fallback?: HTMLElement | null) => {
      cancel();

      const focusFallback = () => {
        if (fallback?.isConnected) fallback.focus();
      };

      // Radix restores focus itself as the overlay unmounts, and its target is
      // the dropdown item that opened us — a node the closing menu already took
      // away, so it resolves to <body>. Its restore runs at the end of the
      // 200ms exit animation, so acting a frame after the close would only get
      // overwritten. Wait for the overlay to actually leave the DOM and move
      // last instead.
      let start: number | null = null;
      let frame = requestAnimationFrame(function poll(time) {
        start ??= time;
        const settled = !document.querySelector(OVERLAY_SELECTOR);
        if (!settled && time - start < OVERLAY_TIMEOUT_MS) {
          frame = requestAnimationFrame(poll);
          return;
        }

        const trigger = triggerRef.current;
        if (!trigger?.isConnected) {
          focusFallback();
          cleanupRef.current = null;
          return;
        }
        trigger.focus();

        // Archiving or deleting a post removes the very card the ⋯ button lives
        // in, but only once the refetch lands — a beat after we focused it. The
        // node then vanishes with focus on it, which is the failure this hook
        // exists to prevent, so keep watching and hand off to the fallback.
        const observer = new MutationObserver(() => {
          if (trigger.isConnected) return;
          stop();
          if (
            document.activeElement === document.body ||
            !document.activeElement?.isConnected
          ) {
            focusFallback();
          }
        });
        observer.observe(document.body, { childList: true, subtree: true });
        const timer = window.setTimeout(stop, TEARDOWN_TIMEOUT_MS);

        function stop() {
          observer.disconnect();
          window.clearTimeout(timer);
          cleanupRef.current = null;
        }
        cleanupRef.current = stop;
      });

      cleanupRef.current = () => cancelAnimationFrame(frame);
    },
    [cancel]
  );

  return { capture, restore };
}
